<!--
  The page you get for a route that does not exist.

  Until now every unmatched URL rendered the nav bar over an empty body — no
  message, no status, nothing. A mistyped link, a route we deleted and a real
  page all looked identical, which made dead links impossible to spot either by
  hand or by a crawler.

  It is a card because that is what this app makes. The shape matches the card
  previews on /build-cards: spine on the left, type line, rules text, flavour.
-->
<template>
  <div class="nf">
    <p class="nf-eyebrow">404</p>
    <h1 class="nf-title">This space isn&rsquo;t on the board</h1>
    <p class="nf-sub">You drew a card that was never in the deck.</p>

    <div class="nf-card" :key="index">
      <div class="nf-spine"><span>{{ card.name }}</span></div>
      <div class="nf-face">
        <p class="nf-type">{{ card.type }}</p>
        <p class="nf-rules">{{ card.rules }}</p>
        <p class="nf-flavor">{{ card.flavor }}</p>
      </div>
    </div>

    <button type="button" class="nf-draw" @click="draw">Draw another</button>

    <div class="nf-actions">
      <router-link to="/" class="btn btn-primary">Back to the table</router-link>
      <router-link to="/my-games" class="btn btn-outline-secondary">My games</router-link>
    </div>

    <p class="nf-path">No route matched <code>{{ path }}</code></p>
  </div>
</template>

<script>
// Five, so "draw another" keeps working for longer than one click.
const DECK = [
  {
    name: 'Lost Wanderer',
    type: 'Corgi · Event',
    rules: 'You have strayed from the board. Lose a turn, then return Home.',
    flavor: 'The castle was that way. Probably.'
  },
  {
    name: 'Missing Page',
    type: 'Item · Rare',
    rules: 'Discard this card. It was never in the deck to begin with.',
    flavor: 'Someone printed the rulebook without it.'
  },
  {
    name: 'Wrong Turn',
    type: 'Terrain',
    rules: 'Move back to the last space you actually remember standing on.',
    flavor: 'The signpost had been eaten.'
  },
  {
    name: 'Typo Goblin',
    type: 'Creature · Pest',
    rules: 'Appears whenever an address is mistyped. Cannot be blocked.',
    flavor: 'It feeds mainly on trailing slashes.'
  },
  {
    name: 'Blank Tile',
    type: 'Component',
    rules: 'This tile has no face. Place it anywhere. It does nothing.',
    flavor: 'Still counts toward the component list.'
  }
]

export default {
  name: 'NotFoundPage',
  data () {
    return {
      // Start somewhere random so two people hitting a dead link do not both
      // get the corgi.
      index: Math.floor(Math.random() * DECK.length)
    }
  },
  computed: {
    card () {
      return DECK[this.index]
    },
    path () {
      return this.$route.fullPath
    }
  },
  methods: {
    draw () {
      // Never redraw the card already showing — a button that looks broken is
      // worse than no button.
      let next = this.index
      while (next === this.index) {
        next = Math.floor(Math.random() * DECK.length)
      }
      this.index = next
    }
  }
}
</script>

<style scoped>
.nf {
  max-width: 30rem;
  margin: 0 auto;
  padding: 3rem 1.25rem 4rem;
  text-align: center;
}

.nf-eyebrow {
  font-family: var(--font-heading, sans-serif);
  font-weight: 700;
  letter-spacing: 0.22em;
  color: var(--pop-orange, #ff6b35);
  margin-bottom: 0.35rem;
}

.nf-title {
  font-family: var(--font-heading, sans-serif);
  color: var(--ink, #2d2d2d);
  font-size: clamp(1.6rem, 5vw, 2.3rem);
  margin-bottom: 0.35rem;
}

.nf-sub {
  font-family: var(--font-handwritten, cursive);
  font-size: 1.3rem;
  color: var(--ink-light, #5a5a5a);
  margin-bottom: 2rem;
}

/* The card itself, in the shape the app already uses for card previews. */
.nf-card {
  display: flex;
  text-align: left;
  margin: 0 auto 1.25rem;
  max-width: 22rem;
  min-height: 13rem;
  background: var(--bg-white, #fefcf9);
  border: 1px solid var(--border-sketch, rgba(0, 0, 0, 0.08));
  border-radius: 10px;
  box-shadow: 0 8px 24px var(--shadow-med, rgba(45, 45, 45, 0.12));
  overflow: hidden;
  transform: rotate(-1.5deg);
  animation: nf-deal 260ms ease-out;
}

@keyframes nf-deal {
  from { opacity: 0; transform: translateY(10px) rotate(3deg); }
  to   { opacity: 1; transform: translateY(0) rotate(-1.5deg); }
}

@media (prefers-reduced-motion: reduce) {
  .nf-card { animation: none; }
}

.nf-spine {
  flex: 0 0 2.2rem;
  background: #1f2430;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0.6rem 0;
}

.nf-spine span {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-family: var(--font-heading, sans-serif);
  font-weight: 600;
  font-size: 0.9rem;
  color: #fefcf9;
  white-space: nowrap;
}

.nf-face {
  flex: 1 1 auto;
  padding: 0.9rem 1rem 1rem;
  display: flex;
  flex-direction: column;
}

.nf-type {
  font-size: 0.72rem;
  font-style: italic;
  color: var(--ink-muted, #8a8a8a);
  margin-bottom: 0.75rem;
}

.nf-rules {
  font-size: 0.95rem;
  line-height: 1.45;
  color: var(--ink, #2d2d2d);
  margin-bottom: 0;
}

.nf-flavor {
  margin-top: auto;
  padding-top: 0.9rem;
  font-size: 0.8rem;
  font-style: italic;
  color: var(--ink-muted, #8a8a8a);
  margin-bottom: 0;
}

.nf-draw {
  background: none;
  border: 1px dashed var(--pencil-light, #d4c9b5);
  border-radius: 999px;
  padding: 0.3rem 1rem;
  font-size: 0.85rem;
  color: var(--ink-light, #5a5a5a);
  margin-bottom: 2rem;
}

.nf-draw:hover {
  border-color: var(--pop-orange, #ff6b35);
  color: var(--pop-orange, #ff6b35);
}

.nf-actions {
  display: flex;
  gap: 0.6rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 2rem;
}

.nf-path {
  font-size: 0.78rem;
  color: var(--ink-muted, #8a8a8a);
  margin-bottom: 0;
}

.nf-path code {
  color: var(--ink-light, #5a5a5a);
  word-break: break-all;
}
</style>
